import { createContext, forwardRef, useContext, useImperativeHandle, useLayoutEffect, useRef } from "react"
import EventTarget from "@ungap/event-target"
import sync, { cancelSync } from "framesync"
import * as pop from "popmotion"
import { VisuallyHidden } from "@react-aria/visually-hidden"

import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"

import useConstant from "hooks/useConstant"

const Context = createContext({})
export const Provider = Context.Provider
export function useTracker() {
  return useContext(Context)
}

function Tracker(
  {
    isFocused,
    tabIndex = 0,
    children,
    wheelDirs: [wheeldirX, wheeldirY, wheeldirZ] = ["x", "y", "z"],
    dragDirs: [dragdirX, dragdirY, dragdirZ] = ["x", "y", "z"],
    naturalScroll = true,
    x: [xmin, xmax, xstep] = [0, 1, 0.01],
    y: [ymin, ymax, ystep] = [0, 1, 0.01],
    z: [zmin, zmax, zstep, zclamp] = [0, 1, 0.01, true],
    clamp = true,
    capturePointer = false,
    capturePointerExceptions = [],
  },
  handle
) {
  const console = useConsole()

  const dictionary = useDictionary()

  const consumer = useTracker()
  const rtrack = useRef()
  const active = useRef(false)

  const et = useConstant(() => new EventTarget())
  const input = useConstant(() => ({ x: 0, y: 0 }))
  const pivot = useConstant(() => ({ x: 0, y: 0, startHrt: null, hrt: null }))
  const ratio = useConstant(() => ({ x: 0, y: 0 }))

  const minmax = {
    x: { min: xmin, max: xmax, step: xstep },
    y: { min: ymin, max: ymax, step: ystep },
    z: { min: zmin, max: zmax, step: zstep, clamp: zclamp },
  }

  const scroll = useConstant(() => {
    let x = minmax.x.min
    let y = minmax.y.min
    let z = minmax.z.min

    return {
      get x() {
        return x
      },
      set x(v) {
        if (v === x) return
        x = v
        const event = new Event("scroll")
        Object.assign(event, { scroll })
        et.dispatchEvent(event)
        consumer?.onscroll?.(scroll)
      },
      get y() {
        return y
      },
      set y(v) {
        if (v === y) return
        y = v
        const event = new Event("scroll")
        Object.assign(event, { scroll })
        et.dispatchEvent(event)
        consumer?.onscroll?.(scroll)
      },
      get z() {
        return z
      },
      set z(v) {
        if (v === z) return
        z = v
        const event = new Event("scroll")
        Object.assign(event, { scroll })
        et.dispatchEvent(event)
        consumer?.onscroll?.(scroll)
      },
    }
  })

  const ctx = {
    addEventListener: (...args) => et.addEventListener(...args),
    removeEventListener: (...args) => et.removeEventListener(...args),
    scroll,
    track() {
      active.current = true
    },
    silent() {
      active.current = false
    },
    force(force = {}) {
      if (Number.isFinite(force.x)) scroll.x = force.x
      if (Number.isFinite(force.y)) scroll.y = force.y
      if (Number.isFinite(force.z)) scroll.z = force.z
    },
    resize: () => {
      const event = new Event("resize")
      et.dispatchEvent(event)
    },
    get node() {
      return rtrack.current
    },
    clientRect: null,
  }

  useImperativeHandle(handle, () => ctx)

  useLayoutEffect(() => {
    const track = rtrack.current
    let clientRect
    let loop
    let scaleLoop

    const onresize = e => {
      clientRect = ctx.clientRect = track.getBoundingClientRect()
      ratio.x = 1
      ratio.y = clientRect.width > clientRect.height ? clientRect.width / clientRect.height : clientRect.height / clientRect.width
    }

    const isHover = e => {
      let target = e.target
      while (true) {
        if (!(target instanceof HTMLElement)) break
        else if (target === track) return true
        else if (
          capturePointerExceptions.reduce((acc, className) => {
            return target.classList.contains(className) || acc
          }, false)
        )
          return false
        target = target?.parentNode
      }

      if (
        capturePointer &&
        global.scrollX + e.clientX >= global.scrollX + clientRect.left &&
        global.scrollX + e.clientX <= global.scrollX + clientRect.right &&
        global.scrollY + e.clientY >= global.scrollY + clientRect.top &&
        global.scrollY + e.clientY <= global.scrollY + clientRect.bottom
      )
        return true
      return false
    }

    const lerp = (start, end, amt) => {
      return Math[end > start ? "min" : "max"](end, (1 - amt) * start + amt * end)
    }

    const onpointerdown = e => {
      if (!isHover(e)) return

      pivot.x = e.clientX
      pivot.y = e.clientY
      pivot.hrt = e.timeStamp
      pivot.start = { x: pivot.x, y: pivot.y, hrt: pivot.hrt }
      pivot.scaleroot = null

      global.addEventListener("contextmenu", onpointerup)
      global.addEventListener("blur", onpointerup)
      if (e.pointerType === "touch" || e.pointerType === "pen") {
        global.addEventListener("touchend", onpointerup)
        global.addEventListener("touchmove", onpointermove, { passive: !(wheeldirZ === "z") })
      } else {
        global.addEventListener("pointercancel", onpointerup)
        global.addEventListener("pointermove", onpointermove, { passive: !(wheeldirZ === "z") })
        global.addEventListener("pointerup", onpointerup)
      }
    }

    const onpointermove = e => {
      if (!active.current) return
      if (e?.touches?.length === 2 && dragdirZ) return onpointerzoom(e)
      if (e?.touches?.length > 1) return
      const scrollModifier = naturalScroll ? 1 : -1
      const multiplier = minmax.x.max / minmax.x.step / clientRect.width // value of one px of track as steps
      const clientX = e?.touches?.[0]?.clientX ?? e.clientX
      const clientY = e?.touches?.[0]?.clientY ?? e.clientY

      const deltaX = -(clientX - pivot.x) * ratio.x * multiplier * scrollModifier
      const deltaY = -(clientY - pivot.y) * ratio.y * multiplier * scrollModifier

      if (dragdirX === dragdirY && dragdirX) {
        input[dragdirX] = scroll[dragdirX] + (deltaX + deltaY)
      } else {
        if (dragdirX) input[dragdirX] = scroll[dragdirX] + deltaX
        if (dragdirY) input[dragdirY] = scroll[dragdirY] + deltaY
      }

      input.hrt = e.timeStamp

      if (e.timeStamp - pivot.start.hrt > 200) pivot.start = { x: pivot.x, y: pivot.y, hrt: pivot.hrt }
      pivot.x = clientX
      pivot.y = clientY
      pivot.hrt = e.timeStamp
      pivot.scaleroot = null

      cancelSync.read(loop)
      loop = sync.read(
        () => {
          if (dragdirX === dragdirY && dragdirX) {
            const x = lerp(scroll[dragdirX], input[dragdirX], minmax[dragdirX].step)
            scroll[dragdirX] = clamp ? Math.max(minmax[dragdirX].min, Math.min(x, minmax[dragdirX].max)) : x
          } else {
            if (dragdirX) {
              const x = lerp(scroll[dragdirX], input[dragdirX], minmax[dragdirX].step)
              scroll[dragdirX] = clamp ? Math.max(minmax[dragdirX].min, Math.min(x, minmax[dragdirX].max)) : x
            }
            if (dragdirY) {
              const y = lerp(scroll[dragdirY], input[dragdirY], minmax[dragdirY].step)
              scroll[dragdirY] = clamp ? Math.max(minmax[dragdirY].min, Math.min(y, minmax[dragdirY].max)) : y
            }
          }
        },
        false,
        false
      )
    }

    const onpointerup = e => {
      global.removeEventListener("contextmenu", onpointerup)
      global.removeEventListener("blur", onpointerup)
      global.removeEventListener("touchend", onpointerup)
      global.removeEventListener("touchmove", onpointermove, { passive: !(wheeldirZ === "z") })
      global.removeEventListener("pointercancel", onpointerup)
      global.removeEventListener("pointermove", onpointermove, { passive: !(wheeldirZ === "z") })
      global.removeEventListener("pointerup", onpointerup)

      if (e.timeStamp - pivot.start.hrt < 200 && (pivot.x !== pivot.start.x || pivot.y !== pivot.start.y)) {
        const multiplier =
          (minmax.x.max / (minmax[dragdirX] ?? minmax[dragdirY] ?? minmax[dragdirZ]).step / clientRect.width) * (1 - (e.timeStamp - pivot.hrt) / 200)
        const length = {
          // length traversed
          x: input.x + (pivot.start.x - pivot.x) * multiplier,
          y: input.y + (pivot.start.y - pivot.y) * multiplier,
        }

        cancelSync.read(loop)
        loop = sync.read(
          ({ delta, timestamp }) => {
            if (timestamp - pivot.hrt > 800) return cancelSync.read(loop) // max duration

            if (dragdirX && dragdirX === dragdirY) {
              const x = lerp(scroll[dragdirX], length[dragdirX], minmax[dragdirX].step * (delta / 100))
              scroll[dragdirX] = clamp ? Math.max(minmax[dragdirX].min, Math.min(x, minmax[dragdirX].max)) : x
            } else {
              if (dragdirX) {
                const x = lerp(scroll[dragdirX], length[dragdirX], minmax[dragdirX].step * (delta / 100))
                scroll[dragdirX] = clamp ? Math.max(minmax[dragdirX].min, Math.min(x, minmax[dragdirX].max)) : x
              }
              if (dragdirY) {
                const y = lerp(scroll[dragdirY], length[dragdirY], minmax[dragdirY].step * (delta / 100))
                scroll[dragdirY] = clamp ? Math.max(minmax[dragdirY].min, Math.min(y, minmax[dragdirY].max)) : y
              }
            }
          },
          true,
          true
        )
      }
    }

    const onpointerzoom = e => {
      e.preventDefault()
      const width = clientRect.width * ratio.x

      const distance = Math.sqrt(Math.pow(e.touches[1].clientX - e.touches[0].clientX, 2) + Math.pow(e.touches[1].clientY - e.touches[0].clientY, 2))
      pivot.scaleroot ??= distance
      const deltaZ = pop.interpolate([-width / 4, width / 4], [-minmax[dragdirZ].max, minmax[dragdirZ].max])(distance - pivot.scaleroot)

      input[dragdirZ] = scroll[dragdirZ] + deltaZ
      input.hrt = e.timeStamp

      cancelSync.read(scaleLoop)
      scaleLoop = sync.read(
        () => {
          pivot.scaleroot = distance

          const z = lerp(scroll[dragdirZ], input[dragdirZ], minmax[dragdirZ].step)
          scroll[dragdirZ] = minmax[dragdirZ].clamp ? Math.max(minmax[dragdirZ].min, Math.min(z, minmax[dragdirZ].max)) : z
        },
        false,
        false
      )
    }

    const onwheel = e => {
      if (!active.current) return
      if (!isHover(e)) return

      if (e.ctrlKey && wheeldirZ) return onwheelzoom(e)

      const scrollModifier = naturalScroll ? 1 : -1
      const width = clientRect.width * ratio.x
      const height = clientRect.height * ratio.y

      let deltaX
      let deltaY

      const shouldBetrackPad =
        !Number.isInteger(e.deltaX) ||
        !Number.isInteger(e.deltaY) ||
        (Math.abs(e.wheelDeltaY) === Math.abs(e.deltaY) && Math.abs(e.wheelDeltaX) === Math.abs(e.deltaX))

      try {
        // wheeldir{X,Y} not always set
        if (e.deltaMode === 0 && shouldBetrackPad) {
          deltaX = pop.interpolate([-width / 2, width / 2], [-minmax[wheeldirX].max, minmax[wheeldirX].max])(e.deltaX * scrollModifier)
          deltaY = pop.interpolate([-height / 2, height / 2], [-minmax[wheeldirY].max, minmax[wheeldirY].max])(e.deltaY * scrollModifier)
        } else {
          deltaX = pop.interpolate([-width / 2, width / 2], [-minmax[wheeldirX].max, minmax[wheeldirX].max])(e.deltaX * minmax[wheeldirX].step * scrollModifier)
          deltaY = pop.interpolate(
            [-height / 2, height / 2],
            [-minmax[wheeldirY].max, minmax[wheeldirY].max]
          )(e.deltaY * minmax[wheeldirY].step * scrollModifier)
        }
      } catch {}

      if (wheeldirX === wheeldirY && wheeldirX) {
        input[wheeldirX] = scroll[wheeldirX] + (deltaX + deltaY)
      } else {
        if (wheeldirX) input[wheeldirX] = scroll[wheeldirX] + deltaX
        if (wheeldirY) input[wheeldirY] = scroll[wheeldirY] + deltaY
      }

      input.hrt = e.timeStamp

      cancelSync.read(loop)
      loop = sync.read(
        ({ delta }) => {
          if (wheeldirX === wheeldirY && wheeldirX) {
            const x = lerp(scroll[wheeldirX], input[wheeldirX], minmax[wheeldirX].step * (delta / 100))
            scroll[wheeldirX] = clamp ? Math.max(minmax[wheeldirX].min, Math.min(x, minmax[wheeldirX].max)) : x
          } else {
            if (wheeldirX) {
              const x = lerp(scroll[wheeldirX], input[wheeldirX], minmax[wheeldirX].step * (delta / 100))
              scroll[wheeldirX] = clamp ? Math.max(minmax[wheeldirX].min, Math.min(x, minmax[wheeldirX].max)) : x
            }

            if (wheeldirY) {
              const y = lerp(scroll[wheeldirY], input[wheeldirY], minmax[wheeldirY].step * (delta / 100))
              scroll[wheeldirY] = clamp ? Math.max(minmax[wheeldirY].min, Math.min(y, minmax[wheeldirY].max)) : y
            }
          }
        },
        false,
        false
      )
    }

    const onwheelzoom = e => {
      e.preventDefault()
      const width = clientRect.width * ratio.x
      const deltaZ = pop.interpolate([-width / 4, width / 4], [-minmax[wheeldirZ].max, minmax[wheeldirZ].max])(-e.deltaY)

      input[wheeldirZ] = scroll[wheeldirZ] + deltaZ
      input.hrt = e.timeStamp

      cancelSync.read(scaleLoop)
      scaleLoop = sync.read(
        () => {
          const z = lerp(scroll[wheeldirZ], input[wheeldirZ], minmax[wheeldirZ].step)
          scroll[wheeldirZ] = minmax[wheeldirZ].clamp ? Math.max(minmax[wheeldirZ].min, Math.min(z, minmax[wheeldirZ].max)) : z
        },
        false,
        false
      )
    }

    function onfocustracker(e) {
      isFocused.set(true)
    }
    function onblurtracker(e) {
      isFocused.set(false)
    }

    onresize()
    global.addEventListener("pointerdown", onpointerdown, { passive: !(wheeldirZ === "z") })
    global.addEventListener("resize", onresize)
    et.addEventListener("resize", onresize)
    global.addEventListener("scroll", onresize)
    global.addEventListener("wheel", onwheel, { passive: !(wheeldirZ === "z") })
    track.addEventListener("focus", onfocustracker)
    track.addEventListener("blur", onblurtracker)

    return () => {
      cancelSync.read(loop)
      cancelSync.read(scaleLoop)
      global.removeEventListener("pointerdown", onpointerdown, { passive: !(wheeldirZ === "z") })
      global.removeEventListener("resize", onresize)
      et.removeEventListener("resize", onresize)
      global.removeEventListener("scroll", onresize)
      global.removeEventListener("wheel", onwheel, { passive: !(wheeldirZ === "z") })
      global.removeEventListener("contextmenu", onpointerup)
      global.removeEventListener("blur", onpointerup)
      global.removeEventListener("touchend", onpointerup)
      global.removeEventListener("touchmove", onpointermove, { passive: !(wheeldirZ === "z") })
      global.removeEventListener("pointercancel", onpointerup)
      global.removeEventListener("pointermove", onpointermove, { passive: !(wheeldirZ === "z") })
      global.removeEventListener("pointerup", onpointerup)
      track.removeEventListener("focus", onfocustracker)
      track.removeEventListener("blur", onblurtracker)
    }
  })

  console.verbose("Tracker(%o)", { minmax })
  return (
    <div ref={rtrack} className='track' tabIndex={tabIndex}>
      {children}
    </div>
  )
}

export default forwardRef(Tracker)
